import { Link } from "react-router-dom";
import { useState } from "react";
import { ArrowUpRight, QrCode, Star, ExternalLink, Plus, Minus, Info } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import { Reveal, Stagger, StaggerItem } from "./Reveal";
import { LINKS, TESTIMONIALS, ADDRESS, BEST_SELLERS, FAQ_GROUPS } from "../lib/constants";

const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

export const ProductCard = ({ p, testPrefix = "product" }) => (
  <a
    href={p.url}
    target="_blank"
    rel="noopener noreferrer"
    data-testid={`${testPrefix}-${slug(p.name)}`}
    className="group flex h-full flex-col border border-white/10 bg-ink p-3 transition-all duration-500 hover:border-gold/50"
  >
    <div className="relative aspect-square overflow-hidden rounded-xl bg-white/5">
      <img src={p.image} alt={p.name} loading="lazy" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
    </div>
    <div className="flex flex-1 flex-col px-1 pb-1 pt-4">
      <p className="text-[11px] uppercase tracking-[0.16em] text-white/40">{p.brand}</p>
      <h3 className="mt-2 text-sm font-medium leading-snug text-white">{p.name}</h3>
      {p.type && (
        <span className="mt-2 inline-block w-fit bg-white/5 px-2 py-1 text-[10px] uppercase tracking-[0.12em] text-gold">{p.type}</span>
      )}
      <span className="mt-3 text-xs text-white/50">{p.thc}</span>
      <div className="mt-2">
        <span className="font-display text-xl text-gold">{p.price}</span>
        <span className="ml-1 text-xs text-white/40">{p.unit}</span>
      </div>
    </div>
  </a>
);

export const ProductGrid = ({ testPrefix }) => (
  <Stagger className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-6">
    {BEST_SELLERS.map((p) => (
      <StaggerItem key={p.name} className="h-full">
        <ProductCard p={p} testPrefix={testPrefix} />
      </StaggerItem>
    ))}
  </Stagger>
);

export const Label = ({ children, className = "" }) => (
  <span className={`inline-block text-xs uppercase tracking-[0.3em] text-gold ${className}`}>{children}</span>
);

export const PageHero = ({ label, title, subtitle, image, testid }) => (
  <section className="relative flex min-h-[52vh] items-end overflow-hidden pb-16 pt-40 md:min-h-[60vh]" data-testid={testid}>
    {image && (
      <div className="absolute inset-0">
        <img src={image} alt="" className="h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/70 to-ink/40" />
      </div>
    )}
    <div className="relative z-10 mx-auto w-full max-w-[1400px] px-6 lg:px-12">
      <Reveal><Label>{label}</Label></Reveal>
      <Reveal delay={0.1}>
        <h1 className="mt-4 max-w-4xl font-display text-4xl font-light uppercase leading-[1.05] tracking-[0.08em] text-white sm:text-5xl lg:text-6xl">
          {title}
        </h1>
      </Reveal>
      {subtitle && (
        <Reveal delay={0.2}>
          <p className="mt-6 max-w-2xl text-base leading-relaxed text-white/65 lg:text-lg">{subtitle}</p>
        </Reveal>
      )}
    </div>
  </section>
);

export const MedicalAccess = () => (
  <section id="medical-access" className="scroll-mt-24 border-t border-white/10 bg-ink-2 py-20 md:py-28" data-testid="medical-access">
    <div className="mx-auto max-w-[1400px] px-6 lg:px-12">
      <Reveal><Label>Medical Cannabis Access</Label></Reveal>
      <Reveal delay={0.1}>
        <h2 className="mt-4 max-w-3xl font-display text-3xl font-light uppercase tracking-[0.08em] text-white md:text-4xl">
          Register for Your Medical Card
        </h2>
      </Reveal>
      <Reveal delay={0.15}>
        <p className="mt-5 max-w-2xl text-white/60">
          Access premium medical cannabis products by registering for your medical card in Washington, DC.
        </p>
      </Reveal>

      <div className="mt-14 grid gap-6 lg:grid-cols-2">
        {[
          { title: "DC Residents", body: "DC residents may purchase medical cannabis from any licensed retailer in DC with a patient registration. Get started with your application today.", href: LINKS.registerDC, cta: "Register as DC Resident", testid: "register-dc" },
          { title: "Non-DC Residents", body: "Non-DC residents may purchase from any licensed retailer in DC by registering for a temporary patient registration or using a valid medical cannabis ID from any state.", href: LINKS.registerNonDC, cta: "Register as Non-Resident", testid: "register-non-dc" },
        ].map((c, i) => (
          <Reveal key={c.title} delay={0.1 * i}>
            <div className="group flex h-full flex-col border border-white/10 bg-ink p-8 transition-all duration-500 hover:border-gold/60 md:p-12">
              <h3 className="font-display text-2xl font-light uppercase tracking-[0.1em] text-white">{c.title}</h3>
              <p className="mt-5 flex-1 leading-relaxed text-white/60">{c.body}</p>
              <a
                href={c.href}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={c.testid}
                className="mt-8 inline-flex items-center gap-2 self-start border-b border-gold pb-1 text-xs uppercase tracking-[0.22em] text-gold transition-all duration-300 group-hover:gap-3"
              >
                {c.cta} <ArrowUpRight className="h-4 w-4" />
              </a>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

export const WaiverBlock = () => (
  <section className="border-t border-white/10 bg-ink py-20 md:py-28" data-testid="waiver-block">
    <div className="mx-auto grid max-w-[1400px] items-center gap-12 px-6 lg:grid-cols-2 lg:px-12">
      <div>
        <Reveal><Label>Welcome to Higher Ground</Label></Reveal>
        <Reveal delay={0.1}>
          <h2 className="mt-4 font-display text-3xl font-light uppercase leading-tight tracking-[0.06em] text-white md:text-4xl">
            Safe Use Treatment Facility &amp; Summer Garden Waiver
          </h2>
        </Reveal>

        <Reveal delay={0.15}>
          <div className="mt-6 flex items-start gap-3 border-l-2 border-gold bg-gold/10 p-4" data-testid="waiver-notice">
            <Info className="mt-0.5 h-5 w-5 flex-shrink-0 text-gold" />
            <p className="text-sm leading-relaxed text-white/85">
              <span className="font-semibold text-gold">No waiver is needed for general entry into Higher Ground.</span>{" "}
              It's only required to access the Safe Use Treatment Facility and Summer Garden.
            </p>
          </div>
        </Reveal>

        <Reveal delay={0.2}>
          <p className="mt-5 max-w-lg leading-relaxed text-white/60">
            Before entering our Safe Use Treatment Facility and Summer Garden, all guests must complete this short waiver.
            The process takes less than one minute and helps ensure a safe, compliant, and enjoyable experience for everyone.
          </p>
        </Reveal>
        <Reveal delay={0.25}>
          <a
            href={LINKS.waiver}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="complete-waiver-btn"
            className="mt-8 inline-flex items-center gap-2 bg-gold px-8 py-4 text-xs font-semibold uppercase tracking-[0.22em] text-ink transition-colors duration-300 hover:bg-champagne"
          >
            Complete Safe Use Waiver <ArrowUpRight className="h-4 w-4" />
          </a>
        </Reveal>
      </div>
      <Reveal delay={0.15}>
        <div className="mx-auto w-full max-w-sm border border-white/10 bg-ink-2 p-8 text-center">
          <p className="text-xs uppercase leading-relaxed tracking-[0.18em] text-gold">
            Accessing the Safe Use Treatment Facility or Summer Garden?
          </p>
          <div className="mx-auto mt-6 flex aspect-square w-full max-w-[240px] items-center justify-center bg-white p-4">
            <img
              src={`https://api.qrserver.com/v1/create-qr-code/?size=280x280&margin=0&data=${encodeURIComponent(LINKS.waiver)}`}
              alt="Scan to complete the Higher Ground DC Safe Use Treatment Facility and Summer Garden waiver"
              className="h-full w-full"
              data-testid="waiver-qr"
            />
          </div>
          <p className="mt-6 flex items-center justify-center gap-2 text-xs leading-relaxed text-white/60">
            <QrCode className="h-4 w-4 flex-shrink-0 text-gold" /> Scan the QR code or use the button to complete your waiver before entering these areas.
          </p>
        </div>
      </Reveal>
    </div>
  </section>
);

export const MapBlock = ({ label = "Find Us" }) => (
  <section className="border-t border-white/10 bg-ink py-20 md:py-28" data-testid="map-block">
    <div className="mx-auto max-w-[1400px] px-6 lg:px-12">
      <div className="grid gap-10 lg:grid-cols-[0.8fr_1.2fr] lg:items-stretch">
        <div className="flex flex-col justify-center">
          <Label>{label}</Label>
          <h2 className="mt-4 font-display text-3xl font-light uppercase tracking-[0.08em] text-white md:text-4xl">
            Higher Ground DC
          </h2>
          <p className="mt-6 text-lg leading-relaxed text-white/70">
            {ADDRESS.line1}<br />{ADDRESS.line2}
          </p>
          <div className="mt-8 flex flex-wrap gap-4">
            <a href={LINKS.google} target="_blank" rel="noopener noreferrer" data-testid="map-directions" className="inline-flex items-center gap-2 bg-gold px-8 py-4 text-xs font-semibold uppercase tracking-[0.22em] text-ink transition-colors hover:bg-champagne">
              Get Directions <ArrowUpRight className="h-4 w-4" />
            </a>
            <a href={`tel:${LINKS.phone}`} className="inline-flex items-center gap-2 border border-white/25 px-8 py-4 text-xs font-semibold uppercase tracking-[0.22em] text-white transition-colors hover:border-gold hover:text-gold">
              (202) 800-1135
            </a>
          </div>
        </div>
        <div className="relative min-h-[360px] overflow-hidden border border-white/10">
          <iframe
            title="Higher Ground DC location map"
            src={LINKS.mapEmbed}
            className="absolute inset-0 h-full w-full grayscale-[0.3]"
            style={{ border: 0, filter: "invert(0.9) hue-rotate(180deg) contrast(0.9)" }}
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            allowFullScreen
            data-testid="map-iframe"
          />
        </div>
      </div>
    </div>
  </section>
);

const FaqItem = ({ q, a, testid }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-white/10" data-testid={testid}>
      <button
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center justify-between gap-6 py-6 text-left"
        data-testid={`${testid}-toggle`}
      >
        <span className="text-base text-white md:text-lg">{q}</span>
        <span className="flex h-8 w-8 flex-shrink-0 items-center justify-center border border-white/15 text-gold transition-colors">
          {open ? <Minus className="h-4 w-4" /> : <Plus className="h-4 w-4" />}
        </span>
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
            className="overflow-hidden"
          >
            <p className="pb-6 pr-14 leading-relaxed text-white/60">{a}</p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export const FaqSection = () => (
  <section className="border-t border-white/10 bg-ink py-20 md:py-28" data-testid="faq-section">
    <div className="mx-auto max-w-[1400px] px-6 lg:px-12">
      <Reveal><Label>Frequently Asked Questions</Label></Reveal>
      <Reveal delay={0.1}>
        <h2 className="mt-4 max-w-2xl font-display text-3xl font-light uppercase tracking-[0.08em] text-white md:text-4xl">
          Everything You Need to Know
        </h2>
      </Reveal>
      <div className="mt-14 grid gap-x-16 gap-y-12 lg:grid-cols-2">
        {FAQ_GROUPS.map((group, gi) => (
          <Reveal key={group.category} delay={0.05 * gi}>
            <p className="mb-2 text-xs uppercase tracking-[0.22em] text-gold">{group.category}</p>
            <div>
              {group.items.map((it, i) => (
                <FaqItem key={it.q} q={it.q} a={it.a} testid={`faq-${gi}-${i}`} />
              ))}
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

export const Testimonials = () => (
  <section className="border-t border-white/10 bg-ink-2 py-20 md:py-28" data-testid="testimonials">
    <div className="mx-auto max-w-[1400px] px-6 lg:px-12">
      <div className="flex flex-col items-start justify-between gap-6 md:flex-row md:items-end">
        <div>
          <Reveal><Label>Testimonials</Label></Reveal>
          <Reveal delay={0.1}>
            <h2 className="mt-4 font-display text-3xl font-light uppercase tracking-[0.08em] text-white md:text-4xl">
              What Our Patients Say
            </h2>
          </Reveal>
        </div>
        <Reveal delay={0.15}>
          <div className="flex items-center gap-3">
            <span className="font-display text-4xl font-light text-gold">4.9</span>
            <div>
              <div className="flex gap-0.5">
                {[...Array(5)].map((_, i) => <Star key={i} className="h-4 w-4 fill-gold text-gold" />)}
              </div>
              <p className="mt-1 text-xs uppercase tracking-[0.15em] text-white/50">Google Business Profile</p>
            </div>
          </div>
        </Reveal>
      </div>

      <div className="mt-14 grid gap-6 md:grid-cols-2 xl:grid-cols-4">
        {TESTIMONIALS.map((t, i) => (
          <Reveal key={t.name} delay={0.08 * i}>
            <div className="flex h-full flex-col border border-white/10 bg-ink p-8">
              <div className="flex gap-0.5">
                {[...Array(5)].map((_, j) => <Star key={j} className="h-3.5 w-3.5 fill-gold text-gold" />)}
              </div>
              <p className="mt-5 flex-1 text-sm leading-relaxed text-white/70">"{t.quote}"</p>
              <div className="mt-6 border-t border-white/10 pt-4">
                <p className="text-sm text-white">{t.name}</p>
                <p className="text-xs text-white/40">{t.when}</p>
              </div>
            </div>
          </Reveal>
        ))}
      </div>

      <Reveal delay={0.1}>
        <a href={LINKS.google} target="_blank" rel="noopener noreferrer" data-testid="google-profile-link" className="mt-10 inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-gold transition-colors hover:text-champagne">
          View Google Business Profile <ExternalLink className="h-4 w-4" />
        </a>
      </Reveal>
    </div>
  </section>
);
